/**
 * Deck legality rules — single source of truth for what makes a deck valid.
 *
 * Rules (per design):
 *  - exactly 1 Champion
 *  - exactly 1 Armor
 *  - exactly 3 Signature Moves
 *  - Equipment: sum of equipmentValue ≤ champion's equipmentStat
 *  - Main deck (Stance + Utility + Attack/Defend): exactly 25 cards
 *  - Stance: max 1 copy of each
 *  - Utility: max 1 copy of each
 *  - Attack/Defend non-special-move: max 2 copies of each
 *  - Attack/Defend special-move (isSpecialMove === true): max 1 copy of each
 *  - Only cards with status "Testing" or "Finalized" may be used.
 */
import type { Card } from "@/types/card";
import type { Deck } from "@/types/deck";

export const MAIN_DECK_SIZE = 25;
export const SIGNATURE_MOVES_REQUIRED = 3;

export function isCardEligibleForDeck(card: Card | undefined): boolean {
  if (!card) return false;
  return card.status === "Testing" || card.status === "Finalized";
}

/** Per-card copy cap based on card type + special-move flag. */
export function copyCapForCard(card: Card | undefined): number {
  if (!card) return 0;
  switch (card.cardType) {
    case "Stance":
      return 1;
    case "Utility":
      return 1;
    case "Attack/Defend":
      return card.attackDefendFields?.isSpecialMove ? 1 : 2;
    default:
      // Champion, Armor, Equipment, Signature Move are not in the main deck;
      // their counts are handled separately. Return 0 so any accidental main
      // deck entry is flagged.
      return 0;
  }
}

/** Parse a numeric stat string safely. Empty / non-numeric → 0. */
function num(value: string | undefined): number {
  if (!value) return 0;
  const n = parseInt(value, 10);
  return Number.isFinite(n) ? n : 0;
}

/** Equipment budget: sum the equipmentValue of all chosen equipment cards. */
export function totalEquipmentValue(equipmentCards: Card[]): number {
  return equipmentCards.reduce((sum, c) => sum + num(c.equipmentFields?.equipmentValue), 0);
}

/** Champion's equipment stat (capacity). */
export function championEquipmentStat(champion: Card | undefined): number {
  return num(champion?.championFields?.equipmentStat);
}

export interface LegalityResult {
  status: "legal" | "illegal";
  errors: string[];
}

export function checkDeckLegality(deck: Deck, cardMap: Map<string, Card>): LegalityResult {
  const errors: string[] = [];

  // ─── Champion ────────────────────────────────────────────────────────────
  const champion = deck.championId ? cardMap.get(deck.championId) : undefined;
  if (!deck.championId) {
    errors.push("Champion required (exactly 1)");
  } else if (!champion) {
    errors.push("Champion not found in card library");
  } else if (champion.cardType !== "Champion") {
    errors.push(`"${champion.name}" is not a Champion card`);
  } else if (!isCardEligibleForDeck(champion)) {
    errors.push(`Champion "${champion.name}" must be Testing or Finalized`);
  }

  // ─── Armor ────────────────────────────────────────────────────────────────
  const armor = deck.armorId ? cardMap.get(deck.armorId) : undefined;
  if (!deck.armorId) {
    errors.push("Armor required (exactly 1)");
  } else if (!armor) {
    errors.push("Armor not found in card library");
  } else if (armor.cardType !== "Armor") {
    errors.push(`"${armor.name}" is not an Armor card`);
  } else if (!isCardEligibleForDeck(armor)) {
    errors.push(`Armor "${armor.name}" must be Testing or Finalized`);
  }

  // ─── Signature moves ─────────────────────────────────────────────────────
  if (deck.signatureMoveIds.length !== SIGNATURE_MOVES_REQUIRED) {
    errors.push(`Need exactly ${SIGNATURE_MOVES_REQUIRED} signature moves (have ${deck.signatureMoveIds.length})`);
  }
  const seenSig = new Set<string>();
  for (const sigId of deck.signatureMoveIds) {
    if (seenSig.has(sigId)) {
      const c = cardMap.get(sigId);
      errors.push(`Duplicate signature move "${c?.name ?? sigId}"`);
    }
    seenSig.add(sigId);
    const c = cardMap.get(sigId);
    if (!c) {
      errors.push(`Signature move ${sigId} not found in card library`);
    } else if (c.cardType !== "Signature Move") {
      errors.push(`"${c.name}" is not a Signature Move card`);
    } else if (!isCardEligibleForDeck(c)) {
      errors.push(`Signature move "${c.name}" must be Testing or Finalized`);
    }
  }

  // ─── Equipment ───────────────────────────────────────────────────────────
  const equipmentCards: Card[] = [];
  const seenEquip = new Set<string>();
  for (const eqId of deck.equipmentIds) {
    if (seenEquip.has(eqId)) {
      const c = cardMap.get(eqId);
      errors.push(`Duplicate equipment "${c?.name ?? eqId}"`);
    }
    seenEquip.add(eqId);
    const c = cardMap.get(eqId);
    if (!c) {
      errors.push(`Equipment ${eqId} not found in card library`);
      continue;
    }
    if (c.cardType !== "Equipment") {
      errors.push(`"${c.name}" is not an Equipment card`);
      continue;
    }
    if (!isCardEligibleForDeck(c)) {
      errors.push(`Equipment "${c.name}" must be Testing or Finalized`);
      continue;
    }
    equipmentCards.push(c);
  }
  const equipUsed = totalEquipmentValue(equipmentCards);
  const equipCap = championEquipmentStat(champion);
  if (equipUsed > equipCap) {
    errors.push(
      `Equipment value ${equipUsed} exceeds champion capacity ${equipCap}`,
    );
  }

  // ─── Main deck size + per-card caps ──────────────────────────────────────
  let mainTotal = 0;
  for (const entry of deck.mainDeck) {
    if (entry.count <= 0) continue;
    mainTotal += entry.count;
    const c = cardMap.get(entry.cardId);
    if (!c) {
      errors.push(`Main deck card ${entry.cardId} not found in card library`);
      continue;
    }
    if (!["Stance", "Utility", "Attack/Defend"].includes(c.cardType)) {
      errors.push(`"${c.name}" cannot be in the main deck (type: ${c.cardType})`);
      continue;
    }
    if (!isCardEligibleForDeck(c)) {
      errors.push(`"${c.name}" must be Testing or Finalized to be in a deck`);
    }
    const cap = copyCapForCard(c);
    if (entry.count > cap) {
      errors.push(`"${c.name}" has ${entry.count} copies (max ${cap})`);
    }
  }
  if (mainTotal !== MAIN_DECK_SIZE) {
    errors.push(`Main deck has ${mainTotal} cards (need exactly ${MAIN_DECK_SIZE})`);
  }

  return { status: errors.length ? "illegal" : "legal", errors };
}
